import styled, { css, keyframes } from 'styled-components'
import { COLORS } from 'consts/colors'
import { TButtonType } from './types'

interface IButtonWrapperProps {
  $isLarge: boolean
  $type: TButtonType
  $hugePadding: boolean
  $isLoading?: boolean
  $isDisabled?: boolean
}

const buttonTypeStyles: Record<TButtonType, ReturnType<typeof css>> = {
  primary: css`
    background: linear-gradient(
      0deg,
      ${COLORS.primaryDark} 0%,
      ${COLORS.primaryExtraDark} 100%
    );
    color: ${COLORS.white};

    &:hover {
      background: linear-gradient(
        180deg,
        ${COLORS.primaryDark} 0%,
        ${COLORS.primaryExtraDark} 100%
      );
      filter: drop-shadow(0px 4px 16px rgba(56, 125, 255, 0.15));
    }
  `,
  secondary: css`
    padding: 12px 20px 11px 20px;
    background: ${COLORS.primaryLight};
    color: ${COLORS.primaryDark};

    &:hover {
      background: ${COLORS.primaryDark};
      color: ${COLORS.white};
    }
  `,
  red: css`
    padding: 12px 20px 11px 20px;
    background: linear-gradient(180deg, #ff706b 0%, #ff4141 100%);
    color: ${COLORS.white};

    &:hover {
      background: linear-gradient(
        180deg,
        ${COLORS.accentRed} 0%,
        ${COLORS.accentRed} 100%
      );
    }
  `,
  white: css`
    padding: 8px 20px;
    background: ${COLORS.white};
    color: ${COLORS.secondary};

    &:hover {
      color: ${COLORS.secondarySemiDark};
    }
  `,
  purple: css`
    padding: 16px 20px;
    background: linear-gradient(180deg, #8980f7 0%, #4136c1 100%);
    color: ${COLORS.white};

    &:hover {
      background: linear-gradient(180deg, #6d63f0 0%, #2e29a6 100%);
    }
  `
}

export const ButtonWrapper = styled.button<IButtonWrapperProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  outline: none;

  ${({ $type }) => buttonTypeStyles[$type]};

  ${({ $type, $hugePadding }) => {
    if ($type === 'primary') {
      return css`
        padding: ${$hugePadding ? '11px 20px' : '8px 32px'};
      `
    }
    if ($type === 'secondary' || $type === 'red') {
      return css`
        padding: 12px 20px 11px 20px;
      `
    }
    if ($type === 'white') {
      return css`
        padding: 8px 20px;
      `
    }
    if ($type === 'purple') {
      return css`
        padding: 16px 20px;
      `
    }
    return ''
  }};

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
      cursor: default;
    `}

  ${({ $isLoading }) =>
    $isLoading &&
    css`
      background: ${COLORS.white};
      border: 1px solid ${COLORS.primaryDark};
      color: ${COLORS.primaryDark};
    `}
`

const loadingAnimation = keyframes`
  0% { opacity: 0.3; }
  50% { opacity: 1; }
  100% { opacity: 0.3; }
`

export const LoadingDots = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '●●●';
    animation: ${loadingAnimation} 1.4s infinite ease-in-out;
    font-size: 12px;
    letter-spacing: 2px;
  }
`
